import React, {forwardRef} from 'react';
import {Link} from 'react-router-dom';
import {ArrowRightIcon} from '@heroicons/react/24/outline';

/*
 * PublicButton — shared CTA for public-facing pages (Homepage, About, Markets,
 * ApplyToJoin, FAQ, PlaygroundInfo). See documentation/internal/VISUAL_IDENTITY.md.
 *
 * Variants:
 *   - primary   solid slate-900 / white-on-dark (high-emphasis CTA)
 *   - secondary hollow outlined (medium-emphasis)
 *   - ghost     no border, hover-tinted (low-emphasis inline action)
 *
 * `withArrow` appends a trailing ArrowRightIcon that nudges right on hover.
 * Reserve it for the primary CTA of a page section.
 *
 * Renders as <Link> when `to` is given, <a> when `href` is given, else <button>.
 */

const BASE =
  'group inline-flex items-center justify-center rounded-md px-5 py-2.5 text-sm font-medium ' +
  'transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-white ' +
  'dark:focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANTS = {
  primary:
    'bg-slate-900 text-white dark:bg-white dark:text-gray-900 hover:opacity-90 shadow-sm',
  secondary:
    'border border-slate-900/15 dark:border-gray-100/15 text-slate-900 dark:text-gray-100 ' +
    'hover:bg-slate-50 dark:hover:bg-gray-800',
  ghost:
    'text-slate-700 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-800',
};

const PublicButton = forwardRef(function PublicButton(
  {variant = 'primary', withArrow = false, to, href, className = '', children, ...rest},
  ref,
) {
  const classes = `${BASE} ${VARIANTS[variant] || VARIANTS.primary} ${className}`.trim();

  const content = (
    <>
      {children}
      {withArrow && (
        <ArrowRightIcon
          aria-hidden="true"
          className="h-4 w-4 ml-1.5 -mr-0.5 transition-transform group-hover:translate-x-0.5"
        />
      )}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button ref={ref} type="button" className={classes} {...rest}>
      {content}
    </button>
  );
});

export default PublicButton;
